import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  DialogActions,
  MenuItem,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { ReactElement, useState } from "react";
import * as Yup from "yup";

import {
  CreateNgpVanIntegrationInput,
  NgpVanIntegration,
  UpdateNgpVanIntegrationInput,
  ValidateNgpVanIntegrationInput,
  NgpVanValidateApiKeyResponse,
} from "~frontend/__generated__/graphql";
import { LinkButton } from "~frontend/components/Link";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import {
  CREATE_NGPVAN_INTEGRATION,
  FETCH_NGPVAN_ITEGRATION_METADATA,
  UPDATE_NGPVAN_INTEGRATION,
  VALIDATE_NGPVAN_INTEGRATION,
} from "~frontend/graphql/integration";
import theme from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

function validateIntegrationValues(
  subaccountId: string,
  values: any
): ValidateNgpVanIntegrationInput {
  const { applicationName, apiKey } = values;
  return {
    subaccountId,
    applicationName,
    apiKey,
  };
}

function createIntegrationValues(
  subaccountId: string,
  values: any
): CreateNgpVanIntegrationInput {
  return {
    subaccountId,
    name: values.name,
    applicationName: values.applicationName,
    apiKey: values.apiKey,
    vanMyCampaignContactTypeId: values.vanMyCampaignContactTypeId,
    vanMyCampaignInitialMessageResultCodeId:
      values.vanMyCampaignInitialMessageResultCodeId,
    vanMyVotersContactTypeId: values.vanMyVotersContactTypeId,
    vanMyVotersInitialMessageResultCodeId:
      values.vanMyVotersInitialMessageResultCodeId,
  };
}

function updateIntegrationValues(
  subaccountId: string,
  integrationId: string,
  values: any
): UpdateNgpVanIntegrationInput {
  return {
    subaccountId,
    integrationId,
    name: values.name,
    applicationName: values.applicationName,
    vanMyCampaignContactTypeId: values.vanMyCampaignContactTypeId,
    vanMyCampaignInitialMessageResultCodeId:
      values.vanMyCampaignInitialMessageResultCodeId,
    vanMyVotersContactTypeId: values.vanMyVotersContactTypeId,
    vanMyVotersInitialMessageResultCodeId:
      values.vanMyVotersInitialMessageResultCodeId,
    ...(values.apiKey ? { apiKey: values.apiKey } : {}),
  };
}

type IntegrationStates = "DRAFT" | "VALIDATED" | "CREATED";
type FormViewStates = "BASIC" | "ADVANCED";

export function NgpVanIntegrationSettingsForm({
  integration,
  subaccountId,
  afterCreate,
  onClose,
}: {
  integration: NgpVanIntegration | null;
  subaccountId: string;
  afterCreate?: () => void;
  onClose: () => void;
}): ReactElement {
  function setInitialValues(integration: NgpVanIntegration | null) {
    if (integration) {
      return {
        ...integration.settings,
        name: integration.name,
        apiKey: "",
      };
    }

    return {
      name: "",
      apiKey: "",
      applicationName: "",
      databaseName: "",
      committeeName: "",
      keyReference: "",
      vanApiInputTypeId: "",
      hasMyCampaign: false,
      vanMyCampaignContactTypeId: "",
      vanMyCampaignInitialMessageResultCodeId: "",
      hasMyVoters: false,
      vanMyVotersContactTypeId: "",
      vanMyVotersInitialMessageResultCodeId: "",
    };
  }

  const editing = !!integration;

  const [integrationState, setIntegrationState] = useState<IntegrationStates>(
    editing ? "CREATED" : "DRAFT"
  );

  const [formView, setFormView] = useState<FormViewStates>("BASIC");

  const [apiKeyValidationData, setApiKeyValidationData] =
    useState<NgpVanValidateApiKeyResponse | null>(null);

  const initialValues = setInitialValues(integration);

  const getErrorMessage = useErrorMessageFormatter();
  const [validateNgpVanIntegration] = useMutation(VALIDATE_NGPVAN_INTEGRATION);
  useQuery(FETCH_NGPVAN_ITEGRATION_METADATA, {
    variables: {
      input: { integrationId: integration?.id, refresh: true },
    },
    skip: !integration,
    onCompleted: data =>
      setApiKeyValidationData(data.fetchNgpVanIntegrationMetadata),
    fetchPolicy: "network-only",
  });

  const [createNgpVanIntegration] = useMutation(CREATE_NGPVAN_INTEGRATION, {
    refetchQueries: ["ListIntegrations"],
    awaitRefetchQueries: true,
  });

  const [updateNgpVanIntegration] = useMutation(UPDATE_NGPVAN_INTEGRATION, {
    refetchQueries: ["ListIntegrations"],
    awaitRefetchQueries: true,
  });

  async function validateIntegration(values: ValidateNgpVanIntegrationInput) {
    const { errors, data } = await validateNgpVanIntegration({
      variables: { input: values },
    });

    if (errors) {
      throw errors;
    }
    return data.validateNgpVanIntegration;
  }

  async function createIntegration(input: CreateNgpVanIntegrationInput) {
    const { errors } = await createNgpVanIntegration({
      variables: { input },
    });
    if (errors) {
      throw errors;
    }
  }

  async function updateIntegration(input: UpdateNgpVanIntegrationInput) {
    const { errors } = await updateNgpVanIntegration({
      variables: { input },
    });
    if (errors) {
      throw errors;
    }
  }

  function toggleFormView() {
    if (formView == "BASIC") {
      setFormView("ADVANCED");
    } else {
      setFormView("BASIC");
    }
  }

  return (
    <Formik
      initialValues={{
        [submitErrorField]: null,
        ...initialValues,
      }}
      validationSchema={validationSchemaGenerator(integrationState)}
      onSubmit={async (values, formik) => {
        function resetAndClose() {
          formik.resetForm();
          if (afterCreate) {
            afterCreate();
          }

          onClose();
        }

        async function createValidatedIntegration(): Promise<void> {
          // submit the actual integration creation request
          await createIntegration(
            createIntegrationValues(subaccountId, values)
          );
          resetAndClose();
        }

        async function validateAndCreateIntegration(): Promise<void> {
          // submit the api for validation / introspection
          const validationData = await validateIntegration(
            validateIntegrationValues(subaccountId, values)
          );

          const newFormValues = {
            ...values,
            ...validationData,
          };

          await formik.setValues(newFormValues);

          setIntegrationState("VALIDATED");
          setApiKeyValidationData(validationData);

          const newDataIsValid =
            await validationSchemaGenerator("VALIDATED").isValid(newFormValues);

          if (newDataIsValid) {
            await createIntegration(
              createIntegrationValues(subaccountId, newFormValues)
            );
            resetAndClose();
          } else {
            // If they form is now invalid, then the issue will be
            // on the advanced tab
            setFormView("ADVANCED");
          }
        }

        async function validateAndUpdateIntegration() {
          // If the apiKey didn't change, we just update the record
          if (
            !values.apiKey &&
            values.applicationName === initialValues.applicationName
          ) {
            await updateIntegration(
              updateIntegrationValues(subaccountId, integration!.id, values)
            );
            resetAndClose();
            return;
          }

          // If the apiKey changed, we need to re-validate the integration
          const validationData = await validateIntegration(
            validateIntegrationValues(subaccountId, values)
          );
          const newFormValues = {
            ...values,
            ...validationData,
          };

          await formik.setValues(newFormValues);

          const newDataIsValid =
            await validationSchemaGenerator("VALIDATED").isValid(newFormValues);

          if (newDataIsValid) {
            await updateIntegration(
              updateIntegrationValues(
                subaccountId,
                integration!.id,
                newFormValues
              )
            );
            resetAndClose();
          } else {
            // If the form is now invalid, then the issue will be
            // on the advanced tab
            setFormView("ADVANCED");
          }
        }

        try {
          switch (integrationState) {
            case "DRAFT":
              await validateAndCreateIntegration();
              break;

            case "VALIDATED":
              await createValidatedIntegration();
              break;

            case "CREATED":
              await validateAndUpdateIntegration();
              break;
          }
        } catch (e) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(e),
          });
        }
      }}
    >
      {({ isSubmitting }) => {
        return (
          <Form>
            <FormikErrorBanner />
            <FormikTextField
              name="name"
              label="Name Your Integration"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              name="applicationName"
              label="Application Name"
              disabled={isSubmitting}
              fullWidth
            />
            <FormikTextField
              label={
                editing ? "API Key (leave blank to keep current)" : "API Key"
              }
              name="apiKey"
              type="password"
              disabled={isSubmitting}
              fullWidth
            />
            {formView == "ADVANCED" && (
              <>
                <Typography sx={{ marginBottom: theme.spacing(2) }}>
                  <strong>Database name</strong>
                  <br />
                  {apiKeyValidationData?.databaseName}
                </Typography>
                <Typography sx={{ marginBottom: theme.spacing(2) }}>
                  <strong>Committee name</strong>
                  <br />
                  {apiKeyValidationData?.committeeName}
                </Typography>
                <Typography sx={{ marginBottom: theme.spacing(2) }}>
                  <strong>Key reference</strong>
                  <br />
                  {apiKeyValidationData?.keyReference}
                </Typography>

                <Box>
                  <Typography variant="body1Bold">
                    My Campaign / EveryAction
                  </Typography>
                </Box>
                {apiKeyValidationData?.hasMyCampaign ? (
                  <>
                    <FormikSelect
                      name="vanMyCampaignContactTypeId"
                      fullWidth
                      label="Contact type"
                    >
                      {apiKeyValidationData &&
                        apiKeyValidationData.vanMyCampaignContactTypes.map(
                          ct => (
                            <MenuItem
                              key={ct.contactTypeId}
                              value={ct.contactTypeId}
                            >
                              {ct.name}
                            </MenuItem>
                          )
                        )}
                    </FormikSelect>
                    <FormikSelect
                      name="vanMyCampaignInitialMessageResultCodeId"
                      fullWidth
                      label="Initial Message Result Code"
                    >
                      {apiKeyValidationData &&
                        apiKeyValidationData.vanMyCampaignResultCodes.map(
                          ct => (
                            <MenuItem
                              key={ct.resultCodeId}
                              value={ct.resultCodeId}
                            >
                              {ct.name}
                            </MenuItem>
                          )
                        )}
                    </FormikSelect>
                  </>
                ) : (
                  <Typography
                    sx={{ padding: theme.spacing(2), fontStyle: "italic" }}
                  >
                    My Campaign / EveryAction access is not enabled
                  </Typography>
                )}
                <Box>
                  <Typography variant="body1Bold">My Voters</Typography>
                  <Field type="hidden" name="hasMyVoters" />
                </Box>
                {apiKeyValidationData?.hasMyVoters ? (
                  <>
                    <FormikSelect
                      name="vanMyVotersContactTypeId"
                      fullWidth
                      label="Contact type"
                    >
                      {apiKeyValidationData &&
                        apiKeyValidationData.vanMyVotersContactTypes.map(ct => (
                          <MenuItem
                            key={ct.contactTypeId}
                            value={ct.contactTypeId}
                          >
                            {ct.name}
                          </MenuItem>
                        ))}
                    </FormikSelect>

                    <FormikSelect
                      name="vanMyVotersInitialMessageResultCodeId"
                      fullWidth
                      label="Initial Message Result Code"
                    >
                      {apiKeyValidationData &&
                        apiKeyValidationData.vanMyVotersResultCodes.map(ct => (
                          <MenuItem
                            key={ct.resultCodeId}
                            value={ct.resultCodeId}
                          >
                            {ct.name}
                          </MenuItem>
                        ))}
                    </FormikSelect>
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{ padding: theme.spacing(2), fontStyle: "italic" }}
                    >
                      My Voters access is not enabled
                    </Typography>
                    <Field type="hidden" name="vanMyVotersContactTypeId" />
                    <Field
                      type="hidden"
                      name="vanMyVotersInitialMessageResultCodeId"
                    />
                  </>
                )}
              </>
            )}

            <DialogActions sx={{ justifyContent: "space-between" }}>
              <Box>
                {integrationState != "DRAFT" && (
                  <LinkButton
                    onClick={toggleFormView}
                    sx={{ textDecoration: "none" }}
                  >
                    {formView == "BASIC" ? "Advanced" : "Basic"}
                  </LinkButton>
                )}
              </Box>
              <Box>
                <Button
                  disabled={isSubmitting}
                  onClick={onClose}
                  color="secondary"
                  sx={{ marginRight: theme.spacing(1) }}
                >
                  Cancel
                </Button>
                <FormikSubmitButton
                  color="primary"
                  variant="contained"
                  {...determineButtonLabels(integrationState)}
                />
              </Box>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
}

function determineButtonLabels(state: IntegrationStates) {
  if (state === "DRAFT") {
    return {
      submitText: "Validate",
      loadingText: "Validating",
    };
  }

  if (state === "VALIDATED") {
    return {
      submitText: "Create",
      loadingText: "Creating...",
    };
  }

  return {
    submitText: "Save",
    loadingText: "Saving...",
  };
}

function validationSchemaGenerator(integrationState: IntegrationStates) {
  switch (integrationState) {
    case "DRAFT":
      return Yup.object({
        name: Yup.string().required("Required"),
        applicationName: Yup.string().required("Required"),
        apiKey: Yup.string().required("Required"),
      });
    case "VALIDATED":
    case "CREATED":
      return Yup.object({
        name: Yup.string().required("Required"),
        applicationName: Yup.string().required("Required"),
        apiKey: Yup.string().optional(),
        vanMyCampaignContactTypeId: Yup.number().nullable(),
        vanMyVotersContactTypeId: Yup.number().nullable(),
        vanMyCampaignInitialMessageResultCodeId: Yup.number().nullable(),
        vanMyVotersInitialMessageResultCodeId: Yup.number().nullable(),
      });
  }
}
